import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useGameContext } from 'features/game/game-context'

export interface IPosition {
  x: number
  y: number
  width: number
  height: number
}

export const startingPosition: IPosition = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
}

export const startingOffset = {
  x: 0,
  y: 0,
}

const pointers: { [key: string]: React.PointerEvent } = {}
let prevDiff = -1

export const useSVGPlane = () => {
  const {
    relativeGameData: { playerColor: player },
  } = useGameContext()
  const svgRef = useRef<SVGSVGElement>(null)
  const [pointerId, setPointerId] = useState(0)
  const [draggable, setDraggable] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState(startingOffset)
  const [zoomFactor, setZoomFactor] = useState({ x: 1, y: 1 })
  const [position, setPosition] = useState<IPosition>(startingPosition)

  const zoomToViewBox = (scale: number, pt?: { x: number; y: number }) => {
    if (!pt) {
      pt = { x: position.x + 0.5 * position.width, y: position.y + 0.5 * position.height }
    }

    const width = scale * position.width
    const height = scale * position.height
    const x = position.x + (pt.x - position.x - scale * (pt.x - position.x))
    const y = position.y + (pt.y - position.y - scale * (pt.y - position.y))

    setPosition({
      x,
      y,
      width,
      height,
    })
  }

  const eventToLocation = (event: { clientX: number; clientY: number }) => {
    if (!svgRef || !svgRef.current) return { x: 0, y: 0 }

    let pt = svgRef.current.createSVGPoint()
    pt.x = event.clientX
    pt.y = event.clientY
    pt = pt.matrixTransform(svgRef.current.getScreenCTM()!.inverse())

    return { x: pt.x, y: pt.y }
  }

  const handleWheel = (event: React.WheelEvent) => {
    if (!svgRef || !svgRef.current) return

    const scaleFactor = Math.pow(1.002, event.deltaY / 2)
    const pt = eventToLocation(event)
    zoomToViewBox(scaleFactor, pt)
  }

  const getClientPos = (event: React.PointerEvent) => {
    return {
      x: event.clientX,
      y: event.clientY,
    }
  }

  const startDrag = (event: React.PointerEvent) => {
    event.preventDefault()
    pointers[event.pointerId] = event

    setDraggable(true)
    setPointerId(event.pointerId)
    setOffset(getClientPos(event))
  }

  const stopDrag = (event: React.PointerEvent) => {
    delete pointers[event.pointerId]
    prevDiff = -1

    setPointerId(0)
    setDraggable(false)
  }

  const handleDrag = (event: React.PointerEvent) => {
    event.preventDefault()

    if (!draggable) return

    pointers[event.pointerId] = event
    const touchesArray: React.PointerEvent[] = Object.values(pointers)

    if (touchesArray.length === 2) {
      const pointerDist = Math.hypot(
        touchesArray[0].clientX - touchesArray[1].clientX,
        touchesArray[0].clientY - touchesArray[1].clientY
      )

      if (prevDiff !== -1) {
        const pt1 = eventToLocation(touchesArray[0])
        const pt2 = eventToLocation(touchesArray[1])
        const midPoint = { x: (pt1.x + pt2.x) / 2, y: (pt1.y + pt2.y) / 2 }
        zoomToViewBox(Math.pow(1.005, prevDiff - pointerDist), midPoint)
      }
      prevDiff = pointerDist
    } else if (touchesArray.length === 1 && pointerId === event.pointerId) {
      const coord = getClientPos(event)

      const newX = position.x - (coord.x - offset.x) * zoomFactor.x
      const newY = position.y - (coord.y - offset.y) * zoomFactor.y

      setOffset(coord)

      setPosition(prev => ({
        ...prev,
        x: newX,
        y: newY,
      }))
    }
  }

  const viewBox = useMemo(() => `${position.x} ${position.y} ${position.width} ${position.height}`, [position])

  useEffect(() => {
    if (svgRef && svgRef.current) {
      setZoomFactor({
        x: position.width / svgRef.current.clientWidth,
        y: position.height / svgRef.current.clientHeight,
      })
    }
  }, [position])

  useEffect(() => {
    const svgWidth = 1900
    const svgHeight = 1900

    const x = player.bottom === 'black' ? -940 : -678
    const y = player.bottom === 'black' ? -768 : -825

    setPosition({
      x,
      y,
      width: svgWidth,
      height: svgHeight,
    })
  }, [player.bottom])

  return {
    svgRef,
    viewBox,
    containerRef,
    stopDrag,
    startDrag,
    handleDrag,
    handleWheel,
  }
}
